import React, { useState } from "react";
import { LuChevronDown, LuDownload, LuFileText } from "react-icons/lu";
import { useNavigate } from "fusion-plugin-react-router";
import { handleDownloadPDF } from "../utils/apiUtils";
import Button from "./Button";

const AccordionItem = ({
  title = "",
  description = "",
  schemeDescriptions = {},
  link = null,
  linkDescription = "",
  isLoading,
  setLoading,
  key = "",
  name = "",
  headerVariant = "default",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const handleDownloadClick = async (e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      await handleDownloadPDF(navigate, link, name);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tw-w-full tw-break-words tw-pl-0">
      {link ? (
        <div className="tw-border-x-0 tw-border-t-0 tw-border-b tw-border-solid tw-border-dis-divider">
          <div
            role="button"
            tabIndex={0}
            aria-expanded={isOpen}
            className="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-3 tw-py-4 tw-text-base tw-font-semibold tw-text-dis-navy"
            onClick={() => setIsOpen(!isOpen)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setIsOpen(!isOpen);
              }
            }}
          >
            <span className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-3">
              <LuFileText
                className="tw-h-5 tw-w-5 tw-shrink-0 tw-text-dis-deep-blue"
                aria-hidden="true"
              />
              <span className="tw-min-w-0 tw-flex-1">{title}</span>
            </span>
            <Button
              aria-label={`Download ${title}`}
              onClick={handleDownloadClick}
              variant="soft"
              size="small"
              leadingIcon={
                !isLoading && <LuDownload aria-hidden="true" strokeWidth={2.5} />
              }
              loading={isLoading}
              loadingText="Downloading"
              className={`tw-shrink-0 tw-px-3 tw-text-dis-small hover:!tw-bg-dis-field ${isLoading ? "!tw-border-dis-field !tw-bg-dis-field !tw-text-dis-navy hover:!tw-bg-dis-field hover:!tw-border-dis-field" : ""}`}
              disabled={isLoading}
            >
              Download
            </Button>
            <LuChevronDown
              aria-hidden="true"
              className={`tw-h-5 tw-w-5 tw-shrink-0 tw-text-dis-deep-blue tw-transition-transform tw-duration-200 ${isOpen ? "tw-rotate-180" : ""}`}
            />
          </div>
          {isOpen && (
            <p className="tw-m-0 tw-whitespace-pre-wrap tw-pb-4 tw-pt-2 tw-text-left tw-text-base tw-leading-[1.5] tw-text-dis-text-muted">
              {linkDescription}
            </p>
          )}
        </div>
      ) : (
        <div>
          <button
            type="button"
            aria-expanded={isOpen}
            className={`tw-mt-4 tw-flex tw-min-h-11 tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-text-left tw-text-dis-navy first:tw-mt-3 ${
              headerVariant === "simple"
                ? "tw-rounded-lg tw-border-0 tw-border-l-[3px] tw-border-solid tw-border-l-dis-sky tw-bg-dis-sky-tint tw-px-3"
                : "tw-rounded-lg tw-border tw-border-solid tw-border-dis-border-strong tw-bg-dis-sky-tint tw-px-3"
            }`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <span className="tw-text-base tw-font-semibold">{title}</span>
            <LuChevronDown
              aria-hidden="true"
              className={`tw-h-5 tw-w-5 tw-text-dis-deep-blue tw-transition-transform tw-duration-200 ${isOpen ? "tw-rotate-180" : ""}`}
            />
          </button>
          {isOpen && (
            <div className="tw-mt-3 tw-text-dis-text-muted">
              <p className="tw-m-0 tw-border-b tw-border-dis-divider tw-pb-3 tw-text-dis-small">
                {description}
              </p>

              {Object.entries(schemeDescriptions)?.map(
                ([key, values], index) => (
                  <div key={index}>
                    {["Header", "Footer", "header", "footer"].includes(key) ? (
                      // Render descriptions without displaying the key
                      Array.isArray(values) ? (
                        values.map((value, i) => {
                          let desc = value; // Assuming value is a string

                          // Check for the "-" prefix
                          if (desc.startsWith("-")) {
                            return (
                              <ul
                                key={i}
                                className="tw-border-b tw-border-dis-divider tw-pb-3"
                              >
                                <li className="tw-text-dis-small tw-font-normal">
                                  {desc.slice(1).trim()}
                                </li>
                              </ul>
                            );
                          }

                          // Check for "##-##" separator
                          if (desc.includes("##-##")) {
                            const [part1, part2] = desc.split("##-##", 2);
                            return (
                              <div
                                key={i}
                                className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-dis-divider"
                              >
                                <p className="tw-m-0 tw-text-dis-small tw-font-normal">
                                  {part1.trim()}
                                </p>
                                <p className="tw-m-0 tw-text-dis-small tw-font-normal">
                                  {part2.trim()}
                                </p>
                              </div>
                            );
                          }

                          // Default case for value
                          return (
                            <p
                              key={i}
                                  className="tw-border-b tw-border-dis-divider tw-pb-3 tw-text-dis-small tw-font-normal"
                            >
                              {desc}
                            </p>
                          );
                        })
                      ) : (
                        <p className="tw-m-0 tw-text-dis-small tw-font-normal">{values}</p>
                      )
                    ) : (
                      // For keys that are not "header" or "footer", render key and descriptions as before
                      <>
                        <h5 className="tw-my-2 tw-border-b tw-border-dis-divider tw-pb-3 tw-text-dis-small tw-font-semibold tw-text-dis-navy">
                          {key}
                        </h5>
                        {Array.isArray(values) ? (
                          values.map((value, i) => {
                            let desc = value; // Assuming value is a string

                            // Check for the "-" prefix
                            if (desc.startsWith("-")) {
                              return (
                                <ul
                                  key={i}
                                  className="tw-border-b tw-border-dis-divider tw-pb-3"
                                >
                                  <li className="tw-text-dis-small tw-font-normal">
                                    {desc.slice(1).trim()}
                                  </li>
                                </ul>
                              );
                            }

                            // Check for "##-##" separator
                            if (desc.includes("##-##")) {
                              const [part1, part2] = desc.split("##-##", 2);
                              return (
                                <div
                                  key={i}
                                  className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-dis-divider"
                                >
                                  <p className="tw-m-0 tw-text-dis-small tw-font-normal">
                                    {part1.trim()}
                                  </p>
                                  <p className="tw-m-0 tw-text-dis-small tw-font-normal">
                                    {part2.trim()}
                                  </p>
                                </div>
                              );
                            }

                            // Default case for value
                            return (
                              <p
                                key={i}
                                className="tw-border-b tw-border-dis-divider tw-pb-3 tw-text-dis-small tw-font-normal"
                              >
                                {desc}
                              </p>
                            );
                          })
                        ) : (
                          <p className="tw-m-0 tw-text-dis-small tw-font-normal">{values}</p>
                        )}
                      </>
                    )}
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Accordion = ({
  items = null,
  key = "",
  title = "",
  link = null,
  linkDescription = "",
  isLoading = true,
  setLoading = () => {},
  name = "",
  header = "",
  headerVariant = "default",
}) => {
  return (
    <div>
      {link ? (
        <div>
          <AccordionItem
            title={title}
            link={link}
            linkDescription={linkDescription}
            isLoading={isLoading}
            setLoading={setLoading}
            key={key}
            name={name}
          />
        </div>
      ) : (
        <>
          {title && (
            <h5 className="tw-mt-6 tw-mb-0 tw-text-base tw-font-semibold tw-text-dis-navy">
              {title}
            </h5>
          )}
          {items?.map((item, index) => (
            <AccordionItem
              key={index}
              title={item.featureName}
              description={item.schemeFeatureDefinition}
              schemeDescriptions={item.schemeDescriptions || {}}
              isLoading={isLoading}
              setLoading={(isLoading) => setLoading(isLoading)}
              headerVariant={headerVariant}
            />
          ))}
        </>
      )}
    </div>
  );
};

export default Accordion;
